(() => {
  'use strict';

  const root = document.querySelector('[data-grownsy-blog-article]');
  if (!root) return;

  const isCompact = () => window.matchMedia('(max-width: 1439px)').matches;

  /* ------------------------------------------------------------
     Desktop sidebar: fixed while reading, fades out before the
     footer newsletter section.
     ------------------------------------------------------------ */
  (() => {
    const sidebar = root.querySelector('.sidebar');
    const layout = root.querySelector('.article-layout');
    // Only the footer signup module, not the newsletter inside the sidebar.
    const footerNewsletter = document.querySelector('.kik-footer-section [data-kik-newsletter]');
    if (!sidebar || !layout || !footerNewsletter) return;

    const desktop = window.matchMedia('(min-width: 1440px)');
    const FADE_DISTANCE = 180;
    const FOOTER_CLEARANCE = 24;
    let raf = 0;

    // Keeps the sidebar's grid cell while the sidebar itself is position:fixed;
    // its viewport coordinates are the sidebar's horizontal anchor.
    const anchor = document.createElement('div');
    anchor.className = 'yj-sidebar-grid-anchor';
    anchor.setAttribute('aria-hidden', 'true');
    anchor.style.cssText = 'grid-column: 2; width: 100%; min-width: 0; height: 0; pointer-events: none;';

    // Bottom edge of the visible sticky/fixed announcement + header (0 when none).
    const headerClearance = () => {
      let bottom = 0;
      document.querySelectorAll('.cc-announcement, .kik-header-section').forEach((el) => {
        const css = getComputedStyle(el);
        const rect = el.getBoundingClientRect();
        if ((css.position === 'sticky' || css.position === 'fixed') &&
            rect.bottom > 0 && rect.top < window.innerHeight &&
            css.visibility !== 'hidden' && css.display !== 'none') {
          bottom = Math.max(bottom, rect.bottom);
        }
      });
      return Math.max(0, Math.ceil(bottom));
    };

    const reset = () => {
      sidebar.classList.remove('is-fixed');
      anchor.remove();
      ['left', 'top', 'width', 'maxHeight', 'opacity', 'pointerEvents'].forEach((prop) => { sidebar.style[prop] = ''; });
    };

    const update = () => {
      raf = 0;
      if (!desktop.matches) return reset();

      const topGap = headerClearance();
      if (layout.getBoundingClientRect().top > topGap) return reset();

      const availableHeight = Math.max(120, window.innerHeight - topGap - 16);
      if (!anchor.isConnected) {
        sidebar.classList.add('is-fixed');
        sidebar.before(anchor);
      }
      const anchorRect = anchor.getBoundingClientRect();
      sidebar.style.left = `${anchorRect.left}px`;
      sidebar.style.top = `${topGap}px`;
      sidebar.style.width = `${anchorRect.width}px`;
      sidebar.style.maxHeight = `${availableHeight}px`;

      // Position-based fade, so it reverses smoothly when scrolling back up.
      const sidebarHeight = Math.min(sidebar.scrollHeight, availableHeight);
      const remaining = footerNewsletter.getBoundingClientRect().top - (topGap + sidebarHeight + FOOTER_CLEARANCE);
      const opacity = Math.max(0, Math.min(1, remaining / FADE_DISTANCE));
      sidebar.style.opacity = String(opacity);
      sidebar.style.pointerEvents = opacity <= 0.05 ? 'none' : '';
    };

    const scheduleUpdate = () => { if (!raf) raf = requestAnimationFrame(update); };
    window.addEventListener('scroll', scheduleUpdate, { passive: true });
    window.addEventListener('resize', scheduleUpdate, { passive: true });
    desktop.addEventListener?.('change', scheduleUpdate);
    window.addEventListener('load', scheduleUpdate, { once: true });
    requestAnimationFrame(update);
  })();

  /* ------------------------------------------------------------
     Table of Article: scroll the heading (not the zero-height
     marker below it) to just under the visible overlays.
     ------------------------------------------------------------ */
  (() => {
    const getAnchor = (hash) => (/^#blog-position-\d+$/.test(hash || '') ? root.querySelector(hash) : null);

    // [[blog_position_N]] is written right AFTER its heading; aim at that heading,
    // skipping empty paragraphs the rich-text editor may have inserted.
    const getTarget = (anchor) => {
      let block = anchor.closest('.content p') || anchor;
      for (let i = 0; i < 5 && block; i++) {
        const previous = block.previousElementSibling;
        if (!previous) break;
        if (previous.matches('h2, h3, h4, h5, h6')) return previous;
        if (previous.matches('p') && !previous.textContent.trim() && !previous.querySelector('img, video, iframe')) {
          block = previous;
          continue;
        }
        break;
      }
      return anchor;
    };

    const visibleOverlayBottom = () => {
      let bottom = 0;
      document.querySelectorAll('.cc-announcement, .kik-header-section').forEach((element) => {
        const style = getComputedStyle(element);
        if (style.display === 'none' || style.visibility === 'hidden') return;
        if (style.position !== 'fixed' && style.position !== 'sticky') return;
        const rect = element.getBoundingClientRect();
        if (rect.top < window.innerHeight && rect.bottom > 0 && rect.top <= bottom + 2) {
          bottom = Math.max(bottom, rect.bottom);
        }
      });
      const mobileToc = isCompact() ? root.querySelector('.mobile-toc-wrap.is-fixed') : null;
      if (mobileToc) {
        const style = getComputedStyle(mobileToc);
        const rect = mobileToc.getBoundingClientRect();
        // The fixed mobile TOC can start below a fixed header, not only at top 0.
        if (style.display !== 'none' && style.visibility !== 'hidden' && rect.bottom > 0) {
          bottom = Math.max(bottom, rect.bottom);
        }
      }
      return Math.max(0, bottom) + 12;
    };

    let navigationId = 0;
    const scrollToBlogPosition = (anchor) => {
      if (!anchor) return;
      const id = ++navigationId;
      const target = getTarget(anchor);
      const align = () => {
        if (id !== navigationId || !target.isConnected) return;
        const desired = Math.max(0, target.getBoundingClientRect().top + window.scrollY - visibleOverlayBottom());
        if (Math.abs(window.scrollY - desired) > 1) window.scrollTo({ top: desired, behavior: 'instant' });
      };
      align();
      // The sticky header changes height while scrolling; re-check after layout settles.
      requestAnimationFrame(() => requestAnimationFrame(align));
      window.setTimeout(align, 180);
      window.setTimeout(align, 450);
    };

    // Toggling the mobile menu cancels any pending re-alignment from a TOC click.
    root.querySelector('.mobile-toc-toggle')?.addEventListener('click', () => { navigationId += 1; });

    root.querySelectorAll('.toc a[href^="#blog-position-"]').forEach((link) => {
      link.addEventListener('click', (event) => {
        const hash = link.getAttribute('href');
        const anchor = getAnchor(hash);
        if (!anchor) return;
        event.preventDefault();
        if (location.hash !== hash) history.pushState(null, '', hash);
        scrollToBlogPosition(anchor);
      });
    });
    window.addEventListener('hashchange', () => scrollToBlogPosition(getAnchor(location.hash)));
    window.addEventListener('load', () => {
      const anchor = getAnchor(location.hash);
      if (anchor) requestAnimationFrame(() => scrollToBlogPosition(anchor));
    }, { once: true });
  })();

  /* ------------------------------------------------------------
     FAQ accordion
     ------------------------------------------------------------ */
  root.querySelectorAll('.faq-row').forEach((button) => {
    button.addEventListener('click', () => {
      const isOpen = button.closest('.faq-item').classList.toggle('is-open');
      button.setAttribute('aria-expanded', String(isOpen));
    });
  });

  /* ------------------------------------------------------------
     Editor's Recommendation: phone progress bar + mouse drag
     ------------------------------------------------------------ */
  (() => {
    const track = root.querySelector('[data-recommend-track]');
    if (!track) return;
    const progress = root.querySelector('[data-recommend-progress]');

    const updateProgress = () => {
      if (!progress) return;
      const scrollable = track.scrollWidth - track.clientWidth;
      if (scrollable <= 0) {
        progress.style.width = '100%';
        progress.style.left = '0';
        return;
      }
      const barWidth = 30;
      progress.style.width = `${barWidth}%`;
      progress.style.left = `${((track.scrollLeft / scrollable) * (100 - barWidth)).toFixed(1)}%`;
    };
    track.addEventListener('scroll', updateProgress, { passive: true });
    window.addEventListener('resize', updateProgress, { passive: true });
    requestAnimationFrame(updateProgress);

    // Drag only becomes a drag after the pointer moves past the threshold.
    // `is-dragging` disables pointer events on the cards, so it must never be
    // set on a plain click/tap, otherwise the product links stop working.
    const DRAG_THRESHOLD = 5;
    let pressed = false;
    let dragging = false;
    let startX = 0;
    let startScroll = 0;

    track.addEventListener('mousedown', (e) => {
      if (e.button !== 0 || track.scrollWidth <= track.clientWidth + 1) return;
      pressed = true;
      dragging = false;
      startX = e.pageX;
      startScroll = track.scrollLeft;
    });

    document.addEventListener('mousemove', (e) => {
      if (!pressed) return;
      const delta = e.pageX - startX;
      if (!dragging) {
        if (Math.abs(delta) < DRAG_THRESHOLD) return;
        dragging = true;
        track.classList.add('is-dragging');
      }
      e.preventDefault();
      track.scrollLeft = startScroll - delta * 1.2;
    });

    document.addEventListener('mouseup', () => {
      pressed = false;
      if (!dragging) return;
      dragging = false;
      // The click that ends a drag has already been targeted at the track
      // (cards ignore pointer events while dragging), so links stay closed.
      requestAnimationFrame(() => track.classList.remove('is-dragging'));
    });

    // Native link/image dragging would swallow the mousemove events.
    track.addEventListener('dragstart', (e) => e.preventDefault());
  })();

  /* ------------------------------------------------------------
     Grouped desktop pagination (≥769px), 3 items per page:
     Related Articles and Editor's Recommendation.
     ------------------------------------------------------------ */
  (() => {
    const desktopQuery = window.matchMedia('(min-width: 769px)');

    const setupGroupedPager = ({ section, cardSelector, pagerSelector, pageSize, equalizeHeight = false }) => {
      const cards = Array.from(section.querySelectorAll(cardSelector));
      const pager = section.querySelector(pagerSelector);
      if (!pager) return;

      const prev = pager.querySelector('[data-page-prev]');
      const next = pager.querySelector('[data-page-next]');
      const status = pager.querySelector('[data-page-status]');
      const totalPages = Math.ceil(cards.length / pageSize);
      const needsPager = cards.length > pageSize;
      const grid = equalizeHeight ? section.querySelector('.recommend-grid') : null;
      let currentPage = 0;

      // Give every product the height of the tallest one (measured with all cards
      // shown), so switching pages never moves the article text below.
      const measureRecommendations = () => {
        if (!grid) return;
        if (!desktopQuery.matches) {
          grid.style.minHeight = '';
          cards.forEach((card) => { card.style.minHeight = ''; });
          return;
        }
        const previousDisplay = cards.map((card) => card.style.display);
        grid.style.minHeight = '';
        cards.forEach((card) => { card.style.minHeight = ''; card.style.display = ''; });
        const tallest = Math.ceil(Math.max(0, ...cards.map((card) => card.getBoundingClientRect().height)));
        const value = tallest ? `${tallest}px` : '';
        cards.forEach((card, i) => { card.style.display = previousDisplay[i]; card.style.minHeight = value; });
        grid.style.minHeight = value;
      };

      const render = () => {
        if (!desktopQuery.matches || !needsPager) {
          cards.forEach((card) => { card.style.display = ''; });
          pager.classList.remove('is-visible');
          if (equalizeHeight && !desktopQuery.matches) measureRecommendations();
          return;
        }

        currentPage = Math.min(currentPage, totalPages - 1);
        const start = currentPage * pageSize;
        cards.forEach((card, index) => {
          card.style.display = index >= start && index < start + pageSize ? '' : 'none';
        });

        pager.classList.add('is-visible');
        if (equalizeHeight) measureRecommendations();
        if (prev) prev.disabled = currentPage === 0;
        if (next) next.disabled = currentPage === totalPages - 1;
        if (status) status.textContent = `${currentPage + 1} / ${totalPages}`;
      };

      prev?.addEventListener('click', () => {
        if (currentPage <= 0) return;
        currentPage -= 1;
        render();
      });
      next?.addEventListener('click', () => {
        if (currentPage >= totalPages - 1) return;
        currentPage += 1;
        render();
      });

      desktopQuery.addEventListener?.('change', render);
      window.addEventListener('resize', render, { passive: true });
      render();
    };

    root.querySelectorAll('.related').forEach((section) => setupGroupedPager({
      section,
      cardSelector: '.related-grid > article',
      pagerSelector: '[data-related-desktop-pagination]',
      pageSize: 3
    }));

    root.querySelectorAll('.recommend').forEach((section) => setupGroupedPager({
      section,
      cardSelector: '.recommend-grid > .product-card',
      pagerSelector: '[data-recommend-desktop-pagination]',
      pageSize: 3,
      equalizeHeight: true
    }));
  })();

  /* ------------------------------------------------------------
     Related Articles: phone carousel indicators
     ------------------------------------------------------------ */
  root.querySelectorAll('.related').forEach((section) => {
    const track = section.querySelector('.related-track');
    const cards = Array.from(section.querySelectorAll('.related-grid > article'));
    const pagination = section.querySelector('.related-pagination');
    if (!track || !pagination || cards.length < 2) return;

    pagination.hidden = false;
    const indicators = cards.map((card, index) => {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = `related-pagination__item${index === 0 ? ' is-active' : ''}`;
      button.setAttribute('aria-label', `Go to related article ${index + 1}`);
      button.setAttribute('aria-current', index === 0 ? 'true' : 'false');
      button.addEventListener('click', () => track.scrollTo({ left: card.offsetLeft, behavior: 'smooth' }));
      pagination.appendChild(button);
      return button;
    });

    let raf = 0;
    const update = () => {
      raf = 0;
      const center = track.scrollLeft + track.clientWidth / 2;
      let active = 0;
      let nearest = Infinity;
      cards.forEach((card, index) => {
        const distance = Math.abs(center - (card.offsetLeft + card.offsetWidth / 2));
        if (distance < nearest) {
          nearest = distance;
          active = index;
        }
      });
      indicators.forEach((item, index) => {
        item.classList.toggle('is-active', index === active);
        item.setAttribute('aria-current', index === active ? 'true' : 'false');
      });
    };

    track.addEventListener('scroll', () => { if (!raf) raf = requestAnimationFrame(update); }, { passive: true });
    window.addEventListener('resize', update, { passive: true });
    update();
  });

  /* ------------------------------------------------------------
     Newsletter: below 1440px a copy of the sidebar card is shown
     in the intro (ids removed to keep them unique).
     ------------------------------------------------------------ */
  (() => {
    const host = root.querySelector('.mobile-intro-newsletter');
    const desktopCard = root.querySelector('.sidebar .newsletter-card');
    if (!host || !desktopCard) return;

    const mountMobileNewsletter = () => {
      if (!isCompact()) {
        host.replaceChildren();
        return;
      }
      if (host.firstElementChild) return;
      const mobileCard = desktopCard.cloneNode(true);
      mobileCard.id = 'yj-blog-newsletter-form-mobile';
      mobileCard.querySelector('#yj-blog-signup')?.removeAttribute('id');
      host.appendChild(mobileCard);
    };
    mountMobileNewsletter();
    window.addEventListener('resize', mountMobileNewsletter, { passive: true });
  })();

  /* ------------------------------------------------------------
     Phone/tablet Table of Article: sticks to the top once scrolled past.
     ------------------------------------------------------------ */
  (() => {
    const wrap = root.querySelector('.mobile-toc-wrap');
    const spacer = root.querySelector('.mobile-toc-spacer');
    const toc = wrap?.querySelector('.mobile-toc');
    const toggle = toc?.querySelector('.mobile-toc-toggle');
    if (!wrap || !spacer || !toc || !toggle) return;

    // Keep the TOC's original height in the document while it is fixed.
    // The spacer's top margin (CSS) also preserves the TOC's original 16px gap.
    const setFixed = (fixed) => {
      spacer.style.height = fixed ? `${wrap.offsetHeight}px` : '0px';
      spacer.classList.toggle('is-active', fixed);
      wrap.classList.toggle('is-fixed', fixed);
    };

    const updateFixedState = () => {
      if (!isCompact()) return setFixed(false);
      // Use the live in-flow position: the TOC before fixing, its spacer after.
      // This stays correct if fonts or images above the TOC load late.
      const isFixed = wrap.classList.contains('is-fixed');
      const anchor = isFixed ? spacer : wrap;
      const shouldFix = anchor.getBoundingClientRect().top <= 0;
      if (shouldFix !== isFixed) setFixed(shouldFix);
      if (shouldFix) spacer.style.height = `${wrap.offsetHeight}px`;
    };

    toggle.addEventListener('click', () => {
      // Near the first anchor the TOC is still in normal flow; collapsing it would
      // move the article up. Keep the reader's text where it is on screen.
      const articleBody = root.querySelector('.article-body');
      const articleRect = articleBody?.getBoundingClientRect();
      const keepPosition = /^#blog-position-\d+$/.test(location.hash) &&
        articleRect && articleRect.top < window.innerHeight && articleRect.bottom > 0;
      const articleTopBefore = keepPosition ? articleRect.top : undefined;

      const isOpen = toc.classList.toggle('is-open');
      toggle.setAttribute('aria-expanded', String(isOpen));
      requestAnimationFrame(() => {
        updateFixedState();
        if (articleTopBefore === undefined) return;
        const change = articleBody.getBoundingClientRect().top - articleTopBefore;
        if (Math.abs(change) > 1) window.scrollBy({ top: change, behavior: 'instant' });
      });
    });

    window.addEventListener('scroll', updateFixedState, { passive: true });
    window.addEventListener('resize', updateFixedState, { passive: true });

    updateFixedState();
  })();

  /* ------------------------------------------------------------
     Newsletter feedback: show "Thank you for subscribing!" in the
     visible (responsive) form for 5 seconds after Shopify confirms.
     ------------------------------------------------------------ */
  (() => {
    const params = new URLSearchParams(location.search);
    const redirectSuccess = params.get('customer_posted') === 'true';
    const success = redirectSuccess || !!root.querySelector('.newsletter-form__message--success');
    const hasError = !!root.querySelector('.newsletter-form__message--error');
    let expired = false;

    const visibleForm = () => {
      const preferred = root.querySelector(isCompact() ? '.mobile-intro-newsletter' : '.sidebar');
      return preferred?.querySelector('[data-yj-newsletter-form]') || root.querySelector('[data-yj-newsletter-form]');
    };

    const syncSuccess = () => {
      root.querySelectorAll('[data-yj-newsletter-form]').forEach((form) => {
        let message = form.querySelector('.newsletter-form__message--success');
        if (!success || expired) {
          message?.remove();
          return;
        }
        if (!message) {
          message = document.createElement('div');
          message.className = 'newsletter-form__message newsletter-form__message--success';
          message.setAttribute('role', 'status');
          message.setAttribute('aria-live', 'polite');
          message.setAttribute('data-yj-newsletter-feedback', '');
          form.appendChild(message);
        }
        message.textContent = 'Thank you for subscribing!';
        form.querySelectorAll('.newsletter-form__message--error').forEach((error) => error.remove());
      });
    };

    root.addEventListener('submit', (event) => {
      const form = event.target.closest?.('[data-yj-newsletter-form]');
      if (!form || !form.checkValidity()) return;
      const button = form.querySelector('[data-yj-newsletter-submit]');
      if (button) button.textContent = 'Signing up…';
    });

    // A layout switch can re-create the mobile copy, so keep it in sync until expiry.
    syncSuccess();
    window.addEventListener('resize', () => requestAnimationFrame(syncSuccess), { passive: true });

    if (!success && !hasError && location.hash !== '#yj-blog-signup') return;

    let positioned = false;
    const positionAndShow = () => {
      syncSuccess();
      const form = visibleForm();
      const target = form?.querySelector('[data-yj-newsletter-feedback]') ||
        form?.querySelector('[data-yj-newsletter-submit]') || form;
      if (!target) return;
      // On success jump instantly, so the 5 seconds aren't spent on a smooth scroll.
      target.scrollIntoView({ behavior: success ? 'instant' : 'smooth', block: 'center', inline: 'nearest' });
      if (positioned || !success) return;
      positioned = true;
      requestAnimationFrame(() => {
        window.setTimeout(() => {
          expired = true;
          root.querySelectorAll('.newsletter-form__message--success').forEach((message) => message.remove());
          if (redirectSuccess && history.replaceState) {
            const url = new URL(location.href);
            url.searchParams.delete('customer_posted');
            history.replaceState(history.state, '', url.pathname + url.search + url.hash);
          }
        }, 5000);
      });
    };

    const afterLayout = () => requestAnimationFrame(() => requestAnimationFrame(positionAndShow));
    if (document.readyState === 'complete') afterLayout();
    else window.addEventListener('load', afterLayout, { once: true });
  })();
})();
